import { isNonStockExpenseCategory } from "@artisancode/api-types";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";

import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DatePicker } from "@/components/shared/date-picker";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useHasPermission } from "@/hooks/use-auth";
import { useExpense } from "@/hooks/use-expenses";
import { useNextNumberPreview } from "@/hooks/use-number-sequences";

import { CostImpactDialog } from "./cost-impact-dialog";
import { ExpenseDetailsSection } from "./expense-details-section";
import { ExpenseItemsTable } from "./expense-items-table";
import { EXPENSE_CATEGORY_LABELS } from "./expense-labels";
import { ExpenseSummaryBox } from "./expense-summary-box";
import { useExpenseForm } from "./use-expense-form";

import type { Expense } from "@artisancode/api-types";

/** Route component for /expenses/new and /expenses/:id/edit. */
export function ExpenseFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: expense, isLoading } = useExpense(id);

  const backToList = () => navigate("/expenses");

  if (id && isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Memuat pengeluaran...
      </div>
    );
  }

  if (id && !expense) {
    return (
      <div className="space-y-4 py-24 text-center">
        <p className="text-sm text-muted-foreground">
          Pengeluaran tidak ditemukan atau sudah dihapus.
        </p>
        <Button variant="outline" onClick={backToList}>
          Kembali ke Daftar
        </Button>
      </div>
    );
  }

  if (expense && (expense.status === "done" || expense.locked)) {
    return (
      <div className="space-y-4 py-24 text-center">
        <p className="text-sm text-muted-foreground">
          {expense.locked
            ? "Pengeluaran ini dibuat dari Penggajian Rider dan tidak bisa diubah di sini."
            : "Pengeluaran yang sudah selesai tidak dapat diubah. Hapus lalu input ulang jika perlu koreksi."}
        </p>
        <Button variant="outline" onClick={backToList}>
          Kembali ke Daftar
        </Button>
      </div>
    );
  }

  return <ExpenseForm editing={expense ?? null} onDone={backToList} />;
}

function ExpenseForm({
  editing,
  onDone,
}: {
  editing: Expense | null;
  onDone: () => void;
}) {
  const {
    form,
    isSaving,
    isUploading,
    selectedFiles,
    existingReceipts,
    addFiles,
    removeSelectedFile,
    removeExistingReceipt,
    fields,
    addItem,
    removeItem,
    itemsSubtotal,
    grandTotal,
    taxOption,
    setTaxOption,
    selectTaxOption,
    discountOption,
    setDiscountOption,
    selectDiscountOption,
    pendingImpact,
    confirmImpact,
    cancelImpact,
    submit,
  } = useExpenseForm(true, editing, onDone);
  const canApprove = useHasPermission("expenses.approve");
  const [confirmDoneOpen, setConfirmDoneOpen] = useState(false);

  const { preview: sequencePreview, autoGenerate } =
    useNextNumberPreview("expense");
  const previewSeq = !editing && autoGenerate ? sequencePreview : "";

  return (
    <div>
      <PageHeader
        title={editing ? "Edit Pengeluaran" : "Tambah Pengeluaran"}
        description="Catat biaya operasional dan barang restock yang masuk stok."
        action={
          <Button variant="outline" size="sm" onClick={onDone}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            Kembali
          </Button>
        }
      />

      <Form {...form}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit("draft")(e);
          }}
        >
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            {/* Left: date, category, items, summary */}
            <div className="min-w-0 space-y-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tanggal</FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Kategori</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={(val) => {
                          field.onChange(val);
                          if (isNonStockExpenseCategory(val)) {
                            const currentItems = form.getValues("items") || [];
                            const updatedItems = currentItems.map((item) => {
                              if (item.itemType) {
                                return {
                                  ...item,
                                  itemType: null,
                                  itemId: null,
                                  uomId: null,
                                };
                              }
                              return item;
                            });
                            form.setValue("items", updatedItems);
                          }
                        }}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.entries(EXPENSE_CATEGORY_LABELS).map(
                            ([v, l]) => (
                              <SelectItem key={v} value={v}>
                                {l}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <ExpenseItemsTable
                form={form}
                fields={fields}
                onAddItem={addItem}
                onRemoveItem={removeItem}
              />

              <ExpenseSummaryBox
                form={form}
                itemsSubtotal={itemsSubtotal}
                grandTotal={grandTotal}
                discountOption={discountOption}
                setDiscountOption={setDiscountOption}
                selectDiscountOption={selectDiscountOption}
                taxOption={taxOption}
                setTaxOption={setTaxOption}
                selectTaxOption={selectTaxOption}
              />
            </div>

            {/* Right: transaction details and attachments */}
            <ExpenseDetailsSection
              form={form}
              editing={editing}
              previewSeq={previewSeq}
              selectedFiles={selectedFiles}
              existingReceipts={existingReceipts}
              isSaving={isSaving}
              addFiles={addFiles}
              removeSelectedFile={removeSelectedFile}
              removeExistingReceipt={removeExistingReceipt}
            />
          </div>

          <div className="sticky bottom-0 z-10 mt-6 flex justify-end gap-2 border-t bg-background/95 py-3 backdrop-blur">
            <Button type="button" variant="outline" onClick={onDone}>
              Batal
            </Button>
            <Button
              type="submit"
              variant={canApprove ? "outline" : "default"}
              disabled={isSaving}
            >
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Mengunggah Berkas...
                </>
              ) : isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Menyimpan...
                </>
              ) : (
                "Simpan Draft"
              )}
            </Button>
            {canApprove && (
              <Button
                type="button"
                disabled={isSaving}
                onClick={() => setConfirmDoneOpen(true)}
              >
                Simpan & Selesaikan
              </Button>
            )}
          </div>
        </form>
      </Form>

      <ConfirmDialog
        open={confirmDoneOpen}
        onOpenChange={setConfirmDoneOpen}
        title="Selesaikan pengeluaran?"
        description="Pengeluaran dibukukan. Bila ada barang stok yang dibeli, stok dan harga rata-rata bahan baku ikut diperbarui."
        confirmLabel="Ya, Selesaikan"
        variant="default"
        isConfirming={isSaving}
        onConfirm={() => {
          setConfirmDoneOpen(false);
          void submit("done")();
        }}
      />

      <CostImpactDialog
        open={!!pendingImpact}
        impact={pendingImpact}
        isSubmitting={isSaving}
        onConfirm={confirmImpact}
        onCancel={cancelImpact}
      />
    </div>
  );
}
